import React, { createContext, useContext, useRef, useEffect, useState, useCallback } from 'react';
import API from './api';

const PollingContext = createContext();

export const usePolling = () => useContext(PollingContext);

const sessionId = () => {
  try { return JSON.parse(localStorage.getItem('session') || '{}')?.client_session_id; }
  catch { return undefined; }
};

export const PollingProvider = ({ children }) => {
  const worker = useRef(null);
  const [pollingResult, setPollingResult] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [, setBriefNotification] = useState(null);

  const createWorker = useCallback(() => {
    const activeWorker = new Worker('/pollingWorker.js?v=2');
    const pending = new Set();
    activeWorker.onmessage = async ({ data: tick }) => {
      if (tick.action !== 'poll' || pending.has(tick.taskId)) return;
      const stop = () => activeWorker.postMessage({ action: 'stop', taskId: tick.taskId });
      if (!tick.sessionId || tick.sessionId !== sessionId()) { stop(); return; }
      pending.add(tick.taskId);
      try {
        const { data } = await API.get(`/task-status/${encodeURIComponent(tick.taskId)}/`);
        if (worker.current !== activeWorker || tick.sessionId !== sessionId()) return;
        if (data.status === 'SUCCESS' || data.status === 'FAILURE') {
          setPollingResult({ status: data.status, result: data.result || { error: data.message }, taskType: tick.taskType });
          stop();
        }
      } catch (error) {
        if (worker.current !== activeWorker || tick.sessionId !== sessionId()) return;
        if ([401, 403, 404].includes(error.response?.status)) {
          setPollingResult({ status: 'FAILURE', result: { error: 'Task access could not be verified.' }, taskType: tick.taskType });
          stop();
        }
        // Transport/service failures remain retryable on the next timer tick.
      } finally { pending.delete(tick.taskId); }
    };
    return activeWorker;
  }, []);

  useEffect(() => {
    worker.current = createWorker();
    return () => {
      worker.current.terminate();
      worker.current = null;
    };
  }, [createWorker]);

  const startPolling = (taskId, taskType) => {
    worker.current?.postMessage({ action: 'start', taskId, taskType, sessionId: sessionId() });
  };

  const stopPolling = () => {
    worker.current.terminate();
    worker.current = createWorker();
  };

  const clearPollingResult = () => {
    setPollingResult(null);
  };

  const showNotification = useCallback((message, type = "success", source = "General") => {
    const newNotification = {
      id: Date.now(),
      message,
      type,
      source,
      timestamp: new Date().toLocaleString(),
      read: false,
    };
    setNotifications((prev) => [...prev, newNotification]);
    setBriefNotification(newNotification);
    setTimeout(() => {
      setBriefNotification(null);
    }, 3000);
  }, []);

  const markNotificationAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const getUnreadCount = () => {
    return notifications.filter((n) => !n.read).length;
  };

  useEffect(() => {
    if (pollingResult) {
      const { status, taskType, result } = pollingResult;
      if (status === 'SUCCESS') {
        if (taskType === 'analyze') {
          showNotification("Prompt analyzed successfully.", "success", "System Prompt Hardening");
        } else if (taskType === 'enhance') {
          if (result && result.message !== "Prompt successfully enhanced and vulnerabilities reduced.") {
            showNotification(result.message, "error", "System Prompt Hardening");
          } else {
            showNotification("Prompt enhanced successfully.", "success", "System Prompt Hardening");
          }
        } else if (taskType === 'scan') {
          showNotification("Scan completed successfully.", "success", "Scan");
        } else if (taskType === 'model_audit') {
            showNotification("Model File Scan successful", "success", "Architecture Analysis");
        }
      } else if (status === 'FAILURE') {

        if (taskType === 'analyze') {
          showNotification(`System Prompt Analysis failed`, "error", "System Prompt Hardening");
        } else if (taskType === 'enhance') {
          showNotification(`System Prompt Enhancement Failed`, "error", "System Prompt Hardening");
        } else if (taskType === 'scan') {
          showNotification(`Scan failed`, "error", "Scan");
        } else if (taskType === 'model_audit') {
            showNotification("Model File Scan Failed", "error", "Architecture Analysis");
        }
      }
    }
  }, [pollingResult, showNotification]);

  return (
    <PollingContext.Provider
      value={{
        startPolling,
        stopPolling,
        pollingResult,
        clearPollingResult,
        showNotification,
        notifications,
        markNotificationAsRead,
        dismissNotification,
        markAllAsRead,
        getUnreadCount,
      }}
    >
      {children}
    </PollingContext.Provider>
  );
};
